import * as React from "react";
import ThreadList from "./components/threadLayoutList";
import ThreadWrapper from "./components/threadWrapper";
import ThreadInput, { POSITION_TYPE } from "./components/threadInput";
import ThreadFilter, { IThreadFilter } from "./components/threadFilter";
import {
  ThreadCount,
  ThreadHeader,
  ThreadInputWrapper,
} from "./components/styledComponents";
import { FormattedPlural, FormattedMessage } from "react-intl";
import PulseLoadingDot from "../pulseLoadingDot";

import { px2rem } from "common/helpers/rem";
import deepEqual from "fast-deep-equal";
import { useIntl } from "react-intl";
import getLoadMoreButtonSet from "./getLoadMoreButtonSet";
import { ThemeContext } from "styled-components";

interface IReplyProps {
  collection: Vingle.ThreadCollectionList;
  onToggleView: (parentThread: Vingle.IThread) => void;
  onLoadMoreData: (
    parentThread: Vingle.IThread,
    pagingDirection: keyof Vingle.IPaging,
    count?: number,
  ) => void;
  onAddReply: (
    parentThread: Vingle.IThread,
    content: string,
    media: Vingle.MediaUpload.IUploadInput | null,
    linkData: Vingle.LinkUploadData | null,
  ) => void;
  disableUpload?: boolean;
  disableLink?: boolean;
}

interface IProps {
  containerId: Vingle.ThreadId;
  name: string;
  threadCounts?: number;
  threads: ReadonlyArray<Vingle.IThread>;
  isLoading?: boolean;
  filter?: IThreadFilter;
  reply?: IReplyProps;
  placeholder?: string;
  inputPositionType?: POSITION_TYPE;
  paging: Vingle.IPaging;
  swapLoadMoreButtonPosition?: boolean;
  disableUpload?: boolean;
  disableLink?: boolean;
  emptyElement?: React.ReactNode;
  wrapperFn?: (element: React.ReactNode) => React.ReactNode;
  handleHighlightUser?: (comment: Vingle.IThread) => boolean;
  onLoadMoreData: (pagingDirection: keyof Vingle.IPaging) => void;
  onLike: (thread: Vingle.IThread) => void;
  onAddData: (
    content: string,
    media: Vingle.MediaUpload.IUploadInput,
    linkData: Vingle.LinkUploadData | null,
  ) => void;
  handleCheckUser: () => boolean;
}

function useThreadRender(props: IProps) {
  const {
    paging,
    onLoadMoreData,
    isLoading,
    threads,
    swapLoadMoreButtonPosition,
    emptyElement,
    reply,
    onLike,
    handleCheckUser,
    threadCounts,
    filter,
    name,
    containerId,
    onAddData,
    placeholder,
    inputPositionType,
    disableLink,
    disableUpload,
    handleHighlightUser,
  } = props;
  const intl = useIntl();
  const theme = React.useContext(ThemeContext);
  let {
    after: afterLoadMoreButton,
    before: beforeLoadMoreButton,
  } = getLoadMoreButtonSet({
    paging,
    name,
    onLoadMoreData,
    isLoading: Boolean(isLoading),
    hasData: Boolean(threads && threads.length),
  });
  if (swapLoadMoreButtonPosition) {
    [afterLoadMoreButton, beforeLoadMoreButton] = [
      beforeLoadMoreButton,
      afterLoadMoreButton,
    ];
  }
  const isEmptyState = Boolean(threads && !threads.length);
  let bodyElement = null;
  if (isEmptyState && isLoading) {
    bodyElement = (
      <div style={{ height: px2rem(100) }}>
        <PulseLoadingDot />
      </div>
    );
  } else if (isEmptyState && emptyElement) {
    bodyElement = emptyElement;
  } else {
    bodyElement =
      threads &&
      threads.map(thread => {
        const replyData = reply && reply.collection[thread.id];
        return (
          <ThreadWrapper
            key={`TreadItem${thread.id}`}
            thread={thread}
            onLike={onLike}
            handleCheckUser={handleCheckUser}
            handleHighlightUser={handleHighlightUser}
            reply={
              reply && {
                threads: replyData && replyData.data,
                visible: Boolean(replyData && replyData.visible),
                paging: replyData ? replyData.paging : {},
                isLoading: Boolean(replyData && replyData.isLoading),
                onToggleView: reply.onToggleView,
                onLoadMoreData: reply.onLoadMoreData,
                onAddThread: reply.onAddReply,
                disableUpload: reply.disableUpload,
                disableLink: reply.disableLink,
              }
            }
          />
        );
      });
  }
  let headerElement = null;
  const showThreadCount = threadCounts !== undefined;
  if (showThreadCount || filter) {
    headerElement = (
      <ThreadHeader>
        {threadCounts !== undefined && (
          <ThreadCount>
            <FormattedPlural
              value={threadCounts}
              one={
                <FormattedMessage
                  id="post_info_comment"
                  values={{ value: threadCounts }}
                />
              }
              other={
                <FormattedMessage
                  id="post_info_comments"
                  values={{ value: threadCounts }}
                />
              }
            />
          </ThreadCount>
        )}
        {filter && <ThreadFilter {...filter} />}
      </ThreadHeader>
    );
  }
  const inputElement = (
    <ThreadInput
      id={containerId}
      onAddThread={onAddData}
      onInteraction={handleCheckUser}
      placeholder={placeholder || intl.formatMessage({ id: "add_a_comment" })}
      backgroundColor={theme.grey100}
      positionType={inputPositionType}
      disableLink={disableLink}
      disableUpload={disableUpload}
    />
  );
  return {
    headerElement,
    bodyElement,
    afterLoadMoreButton,
    beforeLoadMoreButton,
    inputElement,
  };
}

function Thread(props: IProps) {
  const {
    afterLoadMoreButton,
    beforeLoadMoreButton,
    headerElement,
    bodyElement,
    inputElement,
  } = useThreadRender(props);
  const { wrapperFn, inputPositionType } = props;
  const threadElement = (
    <ThreadList
      header={headerElement}
      body={bodyElement}
      top={beforeLoadMoreButton}
      bottom={afterLoadMoreButton}
    />
  );
  return (
    <>
      {wrapperFn ? wrapperFn(threadElement) : threadElement}
      {inputPositionType === POSITION_TYPE.STICKY ? (
        inputElement
      ) : (
        <ThreadInputWrapper>{inputElement}</ThreadInputWrapper>
      )}
    </>
  );
}

export default React.memo(Thread, deepEqual);
export { IReplyProps };
